import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "backButton", "detail", "subtitle", "pagination"]
  static values = {
    title: String,
    endpoint: String,
    analytics: { type: Boolean, default: false },
    emptyText: { type: String, default: "Sin datos" }
  }

  connect() {
    this.chart = null
    this.originalEndpoint = this.endpointValue
    this.drilldownStack = []
    this.currentPage = 1
    this.totalPages = 1
    this.ensureChart()
    this.bindChartClick()
    this.fetchAndRender()
    this.handleResize = this.resize.bind(this)
    window.addEventListener("resize", this.handleResize)
    this.updateBackButton()
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    if (this.chart) {
      this.chart.off("click")
      this.chart.dispose()
      this.chart = null
    }
  }

  async fetchAndRender() {
    if (!this.hasEndpointValue || !this.chart) return

    this.clearDetail()
    this.chart.showLoading()

    try {
      const response = await fetch(this.endpointValue, { headers: { Accept: "application/json" }, cache: "no-store" })
      const data = await response.json()

      if (this.analyticsValue) {
        this.applyHeadTruncation(data)
        this.chart.setOption(data, true)
      } else if (this.isSearchChart()) {
        this.renderSearchDemand(data)
      } else {
        const rows = Array.isArray(data) ? data : []

        if (this.isLostSalesChart()) {
          if (this.isLostSalesDrilldown(rows)) {
            this.renderLostSalesTable(rows)
          } else {
            this.renderLostSales(rows)
          }
        } else if (this.isGeoGapsChart()) {
          this.renderGeoGaps(rows)
        } else {
          this.renderPriceMovements(rows)
        }
      }
    } catch (_error) {
      if (this.analyticsValue) {
        this.chart.setOption({ title: { text: this.emptyTextValue }, series: [] }, true)
      } else if (this.isSearchChart()) {
        this.renderSearchDemand([])
      } else if (this.isLostSalesChart()) {
        this.renderLostSales([])
      } else if (this.isGeoGapsChart()) {
        this.renderGeoGaps([])
      } else {
        this.renderPriceMovements([])
      }
    } finally {
      this.chart.hideLoading()
    }
  }

  goBack(event) {
    event.preventDefault()
    if (this.drilldownStack.length === 0) return

    const previous = this.drilldownStack.pop()
    this.endpointValue = previous.endpoint
    this.updateSubtitle(previous.subtitle || "")
    this.updateBackButton()
    this.fetchAndRender()
  }

  previousPage(event) {
    event.preventDefault()
    if (this.currentPage <= 1) return

    this.goToSearchPage(this.currentPage - 1)
  }

  nextPage(event) {
    event.preventDefault()
    if (this.currentPage >= this.totalPages) return

    this.goToSearchPage(this.currentPage + 1)
  }

  goToSearchPage(page) {
    this.endpointValue = this.withParams(this.endpointValue, { page })
    this.fetchAndRender()
  }

  ensureChart() {
    if (!this.hasCanvasTarget) return
    if (!window.echarts) return
    if (this.chart) return

    this.chart = window.echarts.init(this.canvasTarget)
    this.chart.setOption({
      title: { text: this.titleValue || "Cargando...", left: "center" },
      tooltip: { trigger: "axis" },
      legend: { bottom: 0 },
      xAxis: { type: "category", data: [] },
      yAxis: { type: "value" },
      series: []
    })
  }

  bindChartClick() {
    if (!this.chart) return

    this.chart.off("click")
    this.chart.on("click", (params) => this.handleChartClick(params))
  }

  handleChartClick(params) {
    if (this.isSearchChart()) {
      this.drillIntoSearch(params)
    } else if (this.isLostSalesChart()) {
      this.drillIntoLostSales(params)
    } else if (this.isLegacyPriceChart()) {
      this.drillIntoPrices(params)
    }
  }

  drillIntoPrices(params) {
    const pharmacyId = params?.data?.pharmacyId
    const pharmacyName = params?.seriesName
    if (!pharmacyId || this.currentParams().get("pharmacy_id")) return

    this.pushState()
    this.endpointValue = this.withParams(this.originalEndpoint, { pharmacy_id: pharmacyId })
    this.updateSubtitle(`Farmacia: ${pharmacyName}`)
    this.fetchAndRender()
  }

  drillIntoSearch(params) {
    const query = params?.name
    if (!query || this.currentParams().get("query_filter")) return

    this.pushState()
    this.endpointValue = this.withParams(this.originalEndpoint, { query_filter: query })
    this.updateSubtitle(`Término: ${query}`)
    this.fetchAndRender()
  }

  drillIntoLostSales(params) {
    const query = params?.data?.[2]
    if (!query || this.currentParams().get("query_filter")) return

    this.pushState()
    this.endpointValue = this.withParams(this.originalEndpoint, { query_filter: query })
    this.updateSubtitle(`Término: ${query}`)
    this.fetchAndRender()
  }

  pushState() {
    this.drilldownStack.push({ endpoint: this.endpointValue, subtitle: this.subtitleText() })
    this.updateBackButton()
  }

  currentParams() {
    return new URL(this.endpointValue, window.location.origin).searchParams
  }

  withParams(endpoint, params) {
    const url = new URL(endpoint, window.location.origin)
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        url.searchParams.delete(key)
      } else {
        url.searchParams.set(key, value)
      }
    })
    return `${url.pathname}?${url.searchParams.toString()}`
  }

  resize() {
    if (this.chart) this.chart.resize()
  }

  renderPriceMovements(rows) {
    if (rows.length === 0 || Object.prototype.hasOwnProperty.call(rows[0], "medicine_name")) {
      this.renderPriceHistory(rows)
      return
    }

    const periods = [...new Set(rows.map((row) => row.period))].sort()
    const medicines = [...new Set(rows.map((row) => row.medicine).filter(Boolean))]
    const pharmacies = [...new Set(rows.map((row) => row.pharmacy))]
    const inPharmacyDrilldown = this.currentParams().get("pharmacy_id")

    const finalSeries = inPharmacyDrilldown ? [{
      name: rows[0]?.pharmacy || "Precio promedio",
      type: "bar",
      data: medicines.map((medicine) => {
        const match = rows.find((row) => row.medicine === medicine)
        return match ? { value: match.avg_price, pharmacyId: match.pharmacy_id, medicineId: match.medicine_id } : null
      })
    }] : pharmacies.map((pharmacy) => ({
      name: pharmacy,
      type: "bar",
      data: periods.map((period) => {
        const match = rows.find((row) => row.pharmacy === pharmacy && row.period === period)
        return match ? { value: match.avg_price, pharmacyId: match.pharmacy_id, medicineId: match.medicine_id } : null
      })
    }))

    this.chart.setOption({
      title: { text: this.titleValue || "Movimientos de Precios", left: "center" },
      tooltip: { trigger: "axis" },
      legend: { bottom: 0 },
      xAxis: { type: "category", data: inPharmacyDrilldown ? medicines : periods },
      yAxis: { type: "value", name: "Precio promedio" },
      series: finalSeries,
      graphic: rows.length === 0 ? [this.noDataGraphic()] : []
    }, true)
  }

  renderPriceHistory(rows) {
    const dates = [...new Set(rows.map((row) => row.date))].sort()
    const multiplePharmacies = new Set(rows.map((row) => row.pharmacy_id)).size > 1
    const seriesKeys = [...new Set(rows.map((row) => `${row.medicine_id}:${row.pharmacy_id}`))]

    const series = seriesKeys.map((key) => {
      const groupRows = rows.filter((row) => `${row.medicine_id}:${row.pharmacy_id}` === key)
      const first = groupRows[0]
      const name = multiplePharmacies ? `${first.medicine_name} — ${first.pharmacy_name}` : first.medicine_name

      return {
        name,
        type: "line",
        symbol: "circle",
        showSymbol: true,
        symbolSize: 14,
        data: groupRows.map((row) => [row.date, row.price])
      }
    })

    this.chart.setOption({
      title: { text: this.titleValue || "Inteligencia de Precios", left: "center" },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const value = Array.isArray(params.value) ? params.value[1] : params.value
          const seriesName = this.escapeHtml(params.seriesName || "")
          return `${seriesName}<br/>${this.formatMonthDay(params.name)}<br/>$${Number(value).toFixed(2)} MXN`
        }
      },
      legend: { bottom: 0, type: "scroll" },
      xAxis: { type: "category", data: dates, axisLabel: { formatter: (value) => this.formatMonthDay(value) } },
      yAxis: { type: "value", name: "Precio (MXN)", axisLabel: { formatter: (value) => `$${value}` } },
      series,
      graphic: rows.length === 0 ? [this.noDataGraphic()] : []
    }, true)
  }

  renderSearchDemand(data) {
    const paginated = data && !Array.isArray(data) && Object.prototype.hasOwnProperty.call(data, "rows")
    const rows = paginated ? (Array.isArray(data.rows) ? data.rows : []) : (Array.isArray(data) ? data : [])
    const inQueryDrilldown = this.currentParams().get("query_filter")

    if (paginated) {
      this.currentPage = Number(data.page) || 1
      const perPage = Number(data.per_page) || rows.length || 1
      this.totalPages = Math.max(1, Math.ceil((Number(data.total) || 0) / perPage))
    } else {
      this.currentPage = 1
      this.totalPages = 1
      this.clearPagination()
    }

    if (inQueryDrilldown) {
      this.chart.setOption({
        title: { text: this.titleValue || "Demanda de Búsquedas", left: "center" },
        tooltip: { trigger: "axis" },
        legend: { show: false },
        xAxis: { type: "category", data: rows.map((row) => row.period) },
        yAxis: { type: "value", name: "Búsquedas" },
        series: [{ name: inQueryDrilldown, type: "line", smooth: true, data: rows.map((row) => row.count) }],
        graphic: rows.length === 0 ? [this.noDataGraphic()] : []
      }, true)
      return
    }

    const sorted = [...rows].sort((a, b) => b.count - a.count)
    this.chart.setOption({
      title: { text: this.titleValue || "Demanda de Búsquedas", left: "center" },
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { show: false },
      grid: { left: 220, right: 40, containLabel: false },
      xAxis: { type: "value", name: "Búsquedas" },
      yAxis: { type: "category", data: sorted.map((row) => row.query), inverse: true, axisLabel: { width: 200, overflow: "truncate" } },
      series: [{ type: "bar", data: sorted.map((row) => row.count) }],
      graphic: rows.length === 0 ? [this.noDataGraphic()] : []
    }, true)

    if (paginated) this.updatePagination()
  }

  renderLostSales(rows) {
    const points = rows.map((row) => [row.search_count, row.click_count, row.query])

    this.chart.setOption({
      title: { text: this.titleValue || "Conversión de Búsquedas", left: "center" },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const [searchCount, clickCount, query] = params.data
          return `${this.escapeHtml(query || "")}<br/>Búsquedas: ${searchCount}<br/>Clicks: ${clickCount}<br/>Sin clic: ${searchCount - clickCount}`
        }
      },
      legend: { show: false },
      xAxis: { type: "value", name: "Búsquedas" },
      yAxis: { type: "value", name: "Clicks" },
      series: [{ type: "scatter", symbolSize: 10, data: points }],
      graphic: rows.length === 0 ? [this.noDataGraphic()] : []
    }, true)
  }

  renderLostSalesTable(rows) {
    this.chart.clear()
    this.chart.setOption({ title: { text: this.titleValue || "Conversión de Búsquedas", left: "center" }, graphic: [] }, true)

    if (!this.hasDetailTarget) return

    if (rows.length === 0) {
      this.detailTarget.innerHTML = '<p class="mt-4 text-sm text-gray-500">No se encontraron búsquedas coincidentes.</p>'
      return
    }

    const body = rows.map((row) => `
      <tr class="border-t border-gray-200">
        <td class="px-3 py-2 text-sm text-gray-700">${row.search_id}</td>
        <td class="px-3 py-2 text-sm text-gray-700">${this.escapeHtml(row.city || "")}</td>
        <td class="px-3 py-2 text-sm text-gray-700">${this.escapeHtml(row.searched_at || "")}</td>
        <td class="px-3 py-2 text-sm font-medium ${row.was_clicked ? "text-brand-accent" : "text-red-700"}">${row.was_clicked}</td>
      </tr>
    `).join("")

    this.detailTarget.innerHTML = `
      <div class="mt-4 overflow-x-auto">
        <table class="min-w-full border border-gray-200 text-left">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">ID de búsqueda</th>
              <th class="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">Ciudad</th>
              <th class="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">Buscado</th>
              <th class="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">Con clic</th>
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    `
  }

  renderGeoGaps(rows) {
    const sorted = [...rows].sort((a, b) => b.gap_score - a.gap_score)
    const labels = sorted.map((row) => `${row.city}${row.region ? `, ${row.region}` : ""}`)
    const values = sorted.map((row) => row.gap_score)
    const tooltipData = sorted.map((row) => row)

    this.chart.setOption({
      title: { text: this.titleValue || "Brechas Geográficas", left: "center" },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const idx = params[0].dataIndex
          const row = tooltipData[idx]
          const label = this.escapeHtml(labels[idx] || "")
          return `${label}<br/>Score de brecha: ${row.gap_score}<br/>Búsquedas: ${row.search_count}<br/>Farmacias cercanas: ${row.nearby_pharmacy_count}`
        }
      },
      xAxis: { type: "value", name: "Score de brecha" },
      yAxis: { type: "category", data: labels, axisLabel: { width: 150, overflow: "truncate" } },
      series: [{ type: "bar", data: values, itemStyle: { color: "#f59e0b" } }],
      graphic: rows.length === 0 ? [{ type: "text", left: "center", top: "middle", style: { text: "Sin datos todavía — las búsquedas necesitan ciudad/región", fill: "#6b7280", fontSize: 14 } }] : []
    }, true)
  }

  clearDetail() {
    if (this.hasDetailTarget) this.detailTarget.innerHTML = ""
  }

  updatePagination() {
    if (!this.hasPaginationTarget) return

    this.paginationTarget.innerHTML = `
      <button type="button" data-action="intelligence-chart#previousPage" class="rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50" ${this.currentPage <= 1 ? "disabled" : ""}>Anterior</button>
      <span>Página ${this.currentPage} de ${this.totalPages}</span>
      <button type="button" data-action="intelligence-chart#nextPage" class="rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50" ${this.currentPage >= this.totalPages ? "disabled" : ""}>Siguiente</button>
    `
  }

  clearPagination() {
    if (this.hasPaginationTarget) this.paginationTarget.innerHTML = ""
  }

  updateBackButton() {
    if (!this.hasBackButtonTarget) return
    this.backButtonTarget.classList.toggle("hidden", this.drilldownStack.length === 0)
  }

  updateSubtitle(text) {
    if (this.hasSubtitleTarget) this.subtitleTarget.textContent = text
  }

  subtitleText() {
    return this.hasSubtitleTarget ? this.subtitleTarget.textContent : ""
  }

  isSearchChart() {
    return this.titleIncludes("Demanda de Búsquedas", "Search Demand")
  }

  isLostSalesChart() {
    return this.titleIncludes("Conversión de Búsquedas", "Ventas Perdidas", "Lost Sales")
  }

  isGeoGapsChart() {
    return this.titleIncludes("Brechas Geográficas", "Geo Gaps")
  }

  isLegacyPriceChart() {
    return this.titleIncludes("Movimientos de Precios", "Price Movements")
  }

  titleIncludes(...values) {
    const title = this.titleValue || ""
    return values.some((value) => title.includes(value))
  }

  // Truncate from the tail, not the head, and mark the
  // cut with an ellipsis so it's visibly partial.
  applyHeadTruncation(data) {
    if (!this.isTruncatedLabelChart()) return
    if (!data?.yAxis?.data) return

    data.yAxis.data = this.truncateHeadLabels(data.yAxis.data)
  }

  isTruncatedLabelChart() {
    const pathname = this.endpointPathname()
    return pathname.includes("top_paths") || pathname.includes("top_referrers")
  }

  endpointPathname() {
    try {
      return new URL(this.endpointValue, window.location.origin).pathname
    } catch (_error) {
      return this.endpointValue
    }
  }

  // Truncate every label to maxLength characters from the head, appending an
  // ellipsis only when something was actually cut. A fixed cutoff can make
  // two distinct labels collide once truncated so any pair of
  // different original labels that collide has its shared length grown
  // until they're
  // distinguishable again or one of them is shown in full.
  truncateHeadLabels(labels, maxLength = 28, step = 4) {
    const truncateAt = (label, length) => (label.length <= length ? label : `${label.slice(0, length)}…`)

    const lengths = labels.map(() => maxLength)
    let changed = true

    while (changed) {
      changed = false
      const seenAt = new Map()

      labels.forEach((label, index) => {
        const truncated = truncateAt(label, lengths[index])
        const collisionIndex = seenAt.get(truncated)

        if (collisionIndex === undefined) {
          seenAt.set(truncated, index)
          return
        }

        if (labels[collisionIndex] === label) return

        if (lengths[index] < label.length) {
          lengths[index] += step
          changed = true
        }
        if (lengths[collisionIndex] < labels[collisionIndex].length) {
          lengths[collisionIndex] += step
          changed = true
        }
      })
    }

    return labels.map((label, index) => truncateAt(label, lengths[index]))
  }

  isLostSalesDrilldown(rows) {
    return rows.length > 0 && Object.prototype.hasOwnProperty.call(rows[0], "search_id")
  }

  noDataGraphic() {
    return { type: "text", left: "center", top: "middle", style: { text: this.emptyTextValue, fill: "#6b7280", fontSize: 16 } }
  }

  formatMonthDay(value) {
    if (!value) return ""

    const date = new Date(`${value}T00:00:00`)
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
